/* Private review: bounded graphic motion. Existing hero, origin, product, bag and
   food controllers retain ownership. Event-driven frames only; no timers, storage or I/O. */
(() => {
  'use strict';
  const KEY = '__pbPageMotion';
  if (window[KEY]) { window[KEY].init(document); return; }
  const instances = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const roots = '.dm-page';
  const owned = '[data-pb-motion-owned]';
  const SVG = 'http://www.w3.org/2000/svg';
  const graphics = {
    product: '<g class="pb-motion-orbit__outer"><circle cx="240" cy="240" r="205"/><path d="M96 94A205 205 0 0 1 418 139M440 201A205 205 0 0 1 166 431"/><path d="m412 119 7 21-22-3M183 436l-20-5 12-17"/></g><g class="pb-motion-orbit__inner"><circle cx="240" cy="240" r="174"/><path d="M67 240A174 174 0 0 1 240 66M414 240A174 174 0 0 1 240 414"/></g>',
    refill: '<g class="pb-motion-refill__track"><path d="M143 49A72 72 0 0 1 211 117M211 143A72 72 0 0 1 143 211M117 211A72 72 0 0 1 49 143M49 117A72 72 0 0 1 117 49"/><path d="m201 109 10 9 8-11m-67 95-10 9 10 8m-94-68-9-9-8 10m68-94 9-8-10-8"/></g><g class="pb-motion-refill__trace"><circle cx="130" cy="130" r="81" pathLength="100"/></g><g class="pb-motion-refill__labels" text-anchor="middle"><text x="130" y="25">SHAKE</text><text x="234" y="134" transform="rotate(90 234 134)">FLAKE</text><text x="130" y="244">REFILL</text><text x="27" y="134" transform="rotate(-90 27 134)">REPEAT</text></g><path class="pb-motion-refill__center" d="M111 119c-18-21-42 20-15 25 15 3 29-23 41-25 29-7 30 33 5 25-13-4-20-23-31-25Z"/>',
    usage: '<g class="pb-motion-pinch__plate"><ellipse cx="138" cy="129" rx="100" ry="22"/><path d="M38 129c6 36 191 36 200 0M69 128c19-17 119-17 139 0"/></g>'+[
      [99,121,-6,0,'M-5-1.2l3.8-1.8 5.8 1.5L3.5.2Q.3 2-4.2 1Z'],
      [124,130,9,.12,'M-2.8-2.2l2-.9 3.7 2.5-1.3 3.3-2-.9-2.5-1.3Z'],
      [152,118,-9,.20,'M-4.3.1Q-2.8-2.4.3-2l3.2-1.3L2.4.8-.4 2.1Z'],
      [172,131,6,.29,'M-1.8-.6.1-2 2.1.8.7 1.3Z'],
      [111,135,12,.38,'M-1.4-3.6l3 2.2L1 .6-.9 3-2.2.5Z'],
      [144,137,-7,.46,'M-3-.3l2.4-1.5Q2.7-1.4 3.4.3L1.2 1.6-.2.9Z'],
      [163,125,5,.55,'M-3.8-1.6l3.5-.7 1.1 1.7 2.3-.7-.6 2.8Q-.1 2.3-3.1.8Z']
    ].map(([x,y,r,d,p])=>`<g transform="translate(${x} ${y})"><g class="pb-motion-pinch__grain" style="--pinch-turn:${r}deg;--pinch-delay:${d}s"><path d="${p}"/></g></g>`).join(''),
    compare: '<g class="pb-motion-compare__base"><path d="M20 37q17-12 34 0t34 0t34 0t34 0t34 0t34 0"/><path d="M122 46v33q0 14-14 14H60q-14 0-14 14v25M122 79q0 14 14 14h48q14 0 14 14v25"/><path d="m36 121 10 13 10-13m132 0 10 13 10-13"/><ellipse cx="46" cy="152" rx="30" ry="9"/><ellipse cx="198" cy="152" rx="30" ry="9"/></g><path class="pb-motion-compare__trace" pathLength="100" d="M122 46v33q0 14-14 14H60q-14 0-14 14v25M122 79q0 14 14 14h48q14 0 14 14v25"/>',
    journal: '<g class="pb-motion-journal__back"><path d="m58 24 124 13-13 126-124-13Z"/><path d="m68 39 95 10-8 78-95-10Z"/></g><g class="pb-motion-journal__front"><path d="m101 43 124-12 13 126-124 12Z"/><path d="m115 57 95-9 7 78-95 9Z"/><path d="m125 116 24-22 20 9 19-30 26 29"/><circle cx="138" cy="77" r="7"/></g><path class="pb-motion-journal__route" pathLength="100" d="M24 111c-33 23 8 75 37 54 15-11-1-34-14-18-16 21 10 47 51 39l26-7m-11-5 12 5-8 10"/>'
  };
  function mount(root) {
    if (instances.has(root)) return;
    let destroyed = false;
    const records = new Map();
    const listeners = [];
    const cleanups = [];
    let syncPairResponse = () => {};
    const listen = (target, type, fn, options) => {
      target.addEventListener(type, fn, options);
      listeners.push(() => target.removeEventListener(type, fn, options));
    };
    const intersects = el => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
    };
    const allowed = () => !document.hidden && !reduced.matches && !document.documentElement.classList.contains('dm-paused');
    function sync() {
      if (destroyed) return;
      const run = allowed();
      records.forEach((state, el) => {
        el.dataset.pbMotionActive = String(run && state.visible && el.isConnected);
        el.dataset.pbMotionStill = String(reduced.matches);
      });
      syncPairResponse();
    }
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const state = records.get(entry.target);
        if (state) state.visible = entry.isIntersecting && intersects(entry.target);
      });
      sync();
    }, { threshold: 0, rootMargin: '0px' }) : null;
    function zone(el, kind) {
      if (!el || records.has(el)) return;
      records.set(el, { visible: intersects(el) });
      el.dataset.pbMotionZone = kind;
      el.dataset.pbMotionActive = 'false';
      observer?.observe(el);
    }
    function addGraphic(selector, kind, viewBox, className) {
      const host = root.querySelector(selector);
      if (!host) return;
      const graphic = document.createElementNS(SVG, 'svg');
      graphic.setAttribute('viewBox', viewBox);
      graphic.setAttribute('aria-hidden', 'true');
      graphic.setAttribute('focusable', 'false');
      graphic.setAttribute('class', `pb-motion-graphic ${className}`);
      graphic.setAttribute('data-pb-motion-owned', kind);
      graphic.innerHTML = graphics[kind];
      host.append(graphic);
      zone(host, kind);
    }
    addGraphic('.dm-product-stage', 'product', '0 0 480 480', 'pb-motion-orbit');
    addGraphic('.pb-proof-pair-art', 'product', '0 0 480 480', 'pb-motion-orbit');

    // The lower bottle pair shares the existing orbit lifecycle, but owns only
    // small additive translations. Approved artwork and base rotations stay intact.
    const pair = root.querySelector('.pb-proof-pair-art');
    if (pair) {
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      let pointerX = 0, pointerY = 0, frame = 0;
      const resetPair = () => {
        pointerX = 0; pointerY = 0;
        ['--pb-pair-x', '--pb-pair-y', '--pb-pair-scroll'].forEach(name => pair.style.removeProperty(name));
      };
      const updatePair = () => {
        frame = 0;
        if (destroyed || pair.dataset.pbMotionActive !== 'true') { resetPair(); return; }
        const rect = pair.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1, (window.innerHeight / 2 - rect.top - rect.height / 2) / (window.innerHeight / 2 + rect.height / 2)));
        pair.style.setProperty('--pb-pair-x', `${pointerX.toFixed(2)}px`);
        pair.style.setProperty('--pb-pair-y', `${pointerY.toFixed(2)}px`);
        pair.style.setProperty('--pb-pair-scroll', `${(progress * 6).toFixed(2)}px`);
      };
      const schedulePair = () => { if (!frame && !destroyed && pair.dataset.pbMotionActive === 'true') frame = requestAnimationFrame(updatePair); };
      syncPairResponse = () => { if (frame) cancelAnimationFrame(frame); updatePair(); };
      listen(pair, 'pointermove', event => {
        if (event.pointerType === 'touch' || !finePointer.matches || pair.dataset.pbMotionActive !== 'true') return;
        const rect = pair.getBoundingClientRect();
        pointerX = Math.max(-1, Math.min(1, (event.clientX - rect.left) / rect.width * 2 - 1)) * 8;
        pointerY = Math.max(-1, Math.min(1, (event.clientY - rect.top) / rect.height * 2 - 1)) * 6;
        schedulePair();
      }, { passive: true });
      listen(pair, 'pointerleave', () => { pointerX = 0; pointerY = 0; schedulePair(); });
      listen(finePointer, 'change', () => { pointerX = 0; pointerY = 0; schedulePair(); });
      listen(window, 'scroll', schedulePair, { passive: true });
      listen(window, 'resize', schedulePair, { passive: true });
      cleanups.push(() => { if (frame) cancelAnimationFrame(frame); resetPair(); });
    }
    addGraphic('.pb-refill-heading', 'refill', '0 0 260 260', 'pb-motion-refill');
    addGraphic('.pb-use-heading', 'usage', '0 0 276 180', 'pb-motion-pinch');
    addGraphic('.pb-difference > header', 'compare', '0 0 244 180', 'pb-motion-compare');
    addGraphic('.dm-journal > header', 'journal', '0 0 270 210', 'pb-motion-journal');
    root.querySelectorAll('.pb-compare-row:not(.pb-compare-hero), .pb-visual-row').forEach((el, i) => {
      zone(el, 'rule');
      el.style.setProperty('--pb-rule-delay', `${(i % 5) * .15}s`);
    });
    root.querySelectorAll('.pb-use-panel').forEach(el => zone(el, 'panel'));
    const strip = root.querySelector('.dm-filmstrip');
    zone(strip, 'filmstrip');
    if (strip) {
      const journal = strip.closest('.dm-journal');
      const button = journal?.querySelector('[data-dm-rail-toggle]');
      // Touching a moving photo stops its existing rail, using its existing control.
      // The rail's established click listener remains the only resume handler.
      listen(strip, 'pointerdown', event => {
        if (!['touch', 'pen'].includes(event.pointerType) || !journal || !button) return;
        journal.classList.add('is-paused');
        button.setAttribute('aria-pressed', 'true');
        const label = button.querySelector('[data-dm-rail-label]');
        if (label) label.textContent = button.dataset.play;
      }, { passive: true });
    }
    const classObserver = new MutationObserver(sync);
    classObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    listen(document, 'visibilitychange', sync);
    listen(reduced, 'change', sync);
    if (!observer) {
      const updateVisibility = () => { records.forEach((state, el) => { state.visible = intersects(el); }); sync(); };
      listen(window, 'scroll', updateVisibility, { passive: true });
      listen(window, 'resize', updateVisibility, { passive: true });
    }
    const cleanup = () => {
      if (destroyed) return;
      destroyed = true;
      observer?.disconnect();
      classObserver.disconnect();
      listeners.forEach(remove => remove());
      cleanups.forEach(remove => remove());
      records.forEach((_, el) => {
        delete el.dataset.pbMotionZone;
        delete el.dataset.pbMotionActive;
        delete el.dataset.pbMotionStill;
        el.style.removeProperty('--pb-rule-delay');
      });
      root.querySelectorAll(owned).forEach(el => el.remove());
      instances.delete(root);
    };
    instances.set(root, cleanup);
    sync();
  }
  function init(scope = document) {
    instances.forEach((cleanup, root) => { if (!root.isConnected) cleanup(); });
    // A partial section reload may sit inside the existing page. Rebuild only this
    // enhancement so every new zone has one observer and no stale decorations.
    const enclosing = scope.closest?.(roots);
    if (enclosing && scope !== enclosing) { instances.get(enclosing)?.(); mount(enclosing); return; }
    if (scope.matches?.(roots)) mount(scope);
    scope.querySelectorAll(roots).forEach(mount);
  }
  function destroy(scope = document) {
    instances.forEach((cleanup, root) => {
      if (scope === document || scope === root || scope.contains?.(root) || root.contains?.(scope)) cleanup();
    });
  }
  window[KEY] = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  window.addEventListener('pagehide', () => destroy());
  window.addEventListener('pageshow', () => init());
})();
